import * as layout from '../../components/markLayout.module.css';
import { MarkButton } from '../../components/MarkControls';
import React, { useState, useId } from 'react';
import { useParams, Link } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import BackButton from '../../../../components/BackButton';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useContact from '../../api/useContact';
import useBack from '../../../../hooks/useBack';
import ContactSummary from './ContactSummary';
import MembershipsList from './MembershipsList';
import ActiveDripsList from './ActiveDripsList';
import ProspectEventsList from './ProspectEventsList';
import EngagementTimeline from './EngagementTimeline';
import SuppressionHistoryList from './SuppressionHistoryList';
type DetailTab = 'memberships' | 'drips' | 'prospects';
const TABS: {
    key: DetailTab;
    label: string;
}[] = [
    { key: 'memberships', label: 'Memberships' },
    { key: 'drips', label: 'Active Drips' },
    { key: 'prospects', label: 'Prospect Events' }
];
const ContactDetail: React.FC = () => {
    const { profileId } = useParams<{
        profileId: string;
    }>();
    const back = useBack();
    const [activeTab, setActiveTab] = useState<DetailTab>('memberships');
    const tabId = useId();
    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, key: DetailTab) => {
        const index = TABS.findIndex((tab) => tab.key === key);
        const nextIndex =
            event.key === 'ArrowRight'
                ? (index + 1) % TABS.length
                : event.key === 'ArrowLeft'
                  ? (index + TABS.length - 1) % TABS.length
                  : event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                      ? TABS.length - 1
                      : null;
        if (nextIndex === null) return;
        event.preventDefault();
        const next = TABS[nextIndex].key;
        setActiveTab(next);
        document.getElementById(`${tabId}-tab-${next}`)?.focus();
    };
    const numericId = Number(profileId);
    const isValidId = !!profileId && !isNaN(numericId) && numericId > 0;
    const { data: contactResponse, isLoading, error } = useContact(numericId, isValidId);
    const contact = contactResponse?.data;
    if (isLoading) return <InlineLoader />;
    if (error) return <QueryInlineError error={error} />;
    if (!contact) return <P>Contact not found.</P>;
    const fullName = [contact.first_name, contact.last_name]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' ');
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <BackButton onBack={() => back('/mark/contacts')} alt="Back to contacts" />
            <P
                noDefaultMargin
                size="small"
                styles={{
                    margin: '0.8rem 0',
                    color: 'var(--mark-muted)'
                }}
            >
                <Link
                    to="/mark"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Mark
                </Link>
                {' › '}
                <Link
                    to="/mark/contacts"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Contacts
                </Link>
                {' › '}
                {contact.email}
            </P>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    margin: '0.8rem 0 1.6rem'
                }}
            >
                <H role="h4" noDefaultMargin>
                    {fullName || contact.email}
                </H>
                <P noDefaultMargin size="small">
                    {contact.email}
                </P>
            </div>
            <ContactSummary contact={contact} />
            <div
                role="tablist"
                aria-label="Contact details"
                style={{
                    borderBottom: '1px solid var(--mark-border)',
                    marginTop: '2.4rem'
                }}
                className={layout.tabs}
            >
                {TABS.map((tab) => (
                    <MarkButton
                        key={tab.key}
                        role="tab"
                        tabIndex={activeTab === tab.key ? 0 : -1}
                        onKeyDown={(event) => handleTabKeyDown(event, tab.key)}
                        id={`${tabId}-tab-${tab.key}`}
                        aria-selected={activeTab === tab.key}
                        aria-controls={`${tabId}-panel-${tab.key}`}
                        type="button"
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </MarkButton>
                ))}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-${activeTab}`}
                aria-labelledby={`${tabId}-tab-${activeTab}`}
                style={{
                    padding: '1.6rem 0'
                }}
            >
                {activeTab === 'memberships' && (
                    <MembershipsList memberships={contact.memberships} />
                )}
                {activeTab === 'drips' && <ActiveDripsList drips={contact.active_drips} />}
                {activeTab === 'prospects' && (
                    <ProspectEventsList prospects={contact.prospect_details} />
                )}
            </div>
            <EngagementTimeline key={numericId} profileId={numericId} />
            <SuppressionHistoryList history={contact.suppression_history} />
        </Section>
    );
};
export default ContactDetail;
